// mow-frontend/src/components/analytics/modals/MaintenanceActionModal.js
// FlowSense MOW+ — Schedule Maintenance Work Order Modal
// Version: 1.0
// ─────────────────────────────────────────────────────────────────────────────
// PROPS:
//   isOpen      {bool}    — controlled by useDisclosure in parent
//   onClose     {fn}      — close handler from useDisclosure
//   prediction  {object}  — pre-fill data from the selected asset:
//                             { assetId, assetName, assetType,
//                               healthStatus, riskScore, estimatedMaintenanceCost }
//   onSubmit    {async fn}— parent calls analyticsService.createWorkOrder(payload)
//                           and closes the modal on success; throws on failure
//                           so this modal can display the error inline.
//
// PAYLOAD sent to onSubmit:
//   {
//     asset_id, asset_name, asset_type,
//     work_order_type,          // 'preventive' | 'corrective' | 'emergency'
//     priority,                 // 'critical' | 'high' | 'medium' | 'low'
//     scheduled_date,           // ISO string
//     scheduled_time,           // 'HH:mm'
//     assigned_technician,      // free-text name
//     estimated_duration_hours, // number
//     estimated_cost,           // number
//     description,              // string
//     notes,                    // string
//     source:  'problem_assets_widget',
//     risk_score,
//     health_status,
//   }
//
// FEATURES:
//   - Priority pre-set from prediction.healthStatus / riskScore
//   - Work order type defaults based on severity (critical → corrective, etc.)
//   - Form validation — required fields marked; submit blocked until valid
//   - isMounted ref — no setState after unmount
//   - Loading state during submit with spinner
//   - Inline error alert (modal stays open so operator can fix & retry)
//   - Dark mode via useColorModeValue
//   - Resets form state when modal closes
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  ModalFooter, VStack, HStack, Box, Flex, Text, Icon, IconButton,
  Button, Badge, Divider, FormControl, FormLabel, FormErrorMessage,
  FormHelperText, Input, Select, Textarea, NumberInput,
  NumberInputField, NumberInputStepper, NumberIncrementStepper,
  NumberDecrementStepper, Alert, AlertIcon, AlertTitle, AlertDescription,
  SimpleGrid, Progress, Stat, StatLabel, StatNumber, Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdBuild, MdWarning, MdError, MdCheckCircle, MdSchedule,
  MdAttachMoney, MdTimer, MdPerson, MdNotes, MdPriorityHigh,
  MdInfo, MdClose,
} from 'react-icons/md';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const WORK_ORDER_TYPES = [
  { value: 'corrective',  label: 'Corrective Maintenance',  color: 'red',    description: 'Fix a known failure or defect'          },
  { value: 'preventive',  label: 'Preventive Maintenance',  color: 'blue',   description: 'Scheduled upkeep to prevent failures'   },
  { value: 'emergency',   label: 'Emergency Repair',        color: 'orange', description: 'Urgent unplanned breakdown response'    },
  { value: 'inspection',  label: 'Inspection',              color: 'green',  description: 'Condition assessment, no repair needed' },
];

const PRIORITIES = [
  { value: 'critical', label: 'Critical',      color: 'red',    score: [80, 100] },
  { value: 'high',     label: 'High',          color: 'orange', score: [60,  79] },
  { value: 'medium',   label: 'Medium',        color: 'yellow', score: [40,  59] },
  { value: 'low',      label: 'Low',           color: 'green',  score: [0,   39] },
];

const TECHNICIANS = [
  'Any Available',
  'John Smith',
  'Maria Garcia',
  'David Lee',
  'Sarah Johnson',
  'Michael Brown',
  'Emily Davis',
];

/** Derive a sensible default work order type from the asset's health status. */
const defaultWorkOrderType = (healthStatus) => {
  if (healthStatus === 'critical') return 'corrective';
  if (healthStatus === 'high')     return 'corrective';
  return 'preventive';
};

/** Derive the default priority from health status or risk score. */
const defaultPriority = (healthStatus, riskScore) => {
  if (healthStatus === 'critical' || riskScore >= 80) return 'critical';
  if (healthStatus === 'high'     || riskScore >= 60) return 'high';
  if (healthStatus === 'medium'   || riskScore >= 40) return 'medium';
  return 'low';
};

/** Minimum scheduled date = today */
const todayISO = () => new Date().toISOString().split('T')[0];

// ─────────────────────────────────────────────────────────────────────────────
// SEVERITY SUMMARY CARD
// ─────────────────────────────────────────────────────────────────────────────

const SeverityBanner = React.memo(({ prediction, statBg, borderColor }) => {
  const priority = PRIORITIES.find(p => p.value === defaultPriority(prediction?.healthStatus, prediction?.riskScore));
  const riskPct  = Math.min(prediction?.riskScore || 0, 100);

  return (
    <Box p={4} bg={statBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
      <Flex justify="space-between" align="flex-start" mb={3}>
        <VStack align="flex-start" spacing={0}>
          <Text fontWeight="bold" fontSize="sm">{prediction?.assetName || 'Unknown Asset'}</Text>
          <HStack spacing={2} mt={0.5}>
            <Badge colorScheme="gray"    fontSize="xs">{prediction?.assetType  || 'N/A'}</Badge>
            <Badge colorScheme={priority?.color || 'gray'} fontSize="xs">
              {priority?.label || 'Unknown'} Severity
            </Badge>
          </HStack>
        </VStack>
        <Stat textAlign="right" maxW="100px">
          <StatLabel fontSize="10px">Risk Score</StatLabel>
          <StatNumber fontSize="2xl" color={`${priority?.color || 'gray'}.500`}>{riskPct}</StatNumber>
        </Stat>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={2}>Risk severity</Text>
      <Progress
        value={riskPct}
        colorScheme={priority?.color || 'gray'}
        size="sm"
        borderRadius="full"
        bg="gray.100"
        hasStripe={riskPct >= 80}
        isAnimated={riskPct >= 80}
      />
      {prediction?.estimatedMaintenanceCost > 0 && (
        <HStack mt={3} spacing={1} fontSize="xs" color="gray.500">
          <Icon as={MdAttachMoney} />
          <Text>Est. maintenance cost: <strong>${prediction.estimatedMaintenanceCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong></Text>
        </HStack>
      )}
    </Box>
  );
});
SeverityBanner.displayName = 'SeverityBanner';

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────

const MaintenanceActionModal = ({ isOpen, onClose, prediction, onSubmit }) => {

  // ── Theme ──────────────────────────────────────────────────────────────────
  const cardBg      = useColorModeValue('white',    'gray.800');
  const statBg      = useColorModeValue('gray.50',  'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor= useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const labelColor  = useColorModeValue('gray.700', 'gray.200');

  // ── Derived defaults from prediction prop ──────────────────────────────────
  const defaultType = useMemo(
    () => defaultWorkOrderType(prediction?.healthStatus),
    [prediction?.healthStatus]
  );
  const defaultPrio = useMemo(
    () => defaultPriority(prediction?.healthStatus, prediction?.riskScore),
    [prediction?.healthStatus, prediction?.riskScore]
  );

  // ── Form state ─────────────────────────────────────────────────────────────
  const emptyForm = useCallback(() => ({
    workOrderType:           defaultType,
    priority:                defaultPrio,
    scheduledDate:           todayISO(),
    scheduledTime:           '08:00',
    assignedTechnician:      'Any Available',
    estimatedDurationHours:  2,
    estimatedCost:           prediction?.estimatedMaintenanceCost || 0,
    description:             '',
    notes:                   '',
  }), [defaultType, defaultPrio, prediction?.estimatedMaintenanceCost]);

  const [form,       setForm]       = useState(emptyForm);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError,setSubmitError]= useState(null);
  const [submitDone, setSubmitDone] = useState(false);

  const isMounted = useRef(true);

  // Reset form whenever modal opens with a new asset
  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm());
      setErrors({});
      setSubmitError(null);
      setSubmitDone(false);
    }
  }, [isOpen, emptyForm]);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ── Field update ───────────────────────────────────────────────────────────
  const setField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, []);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const e = {};
    if (!form.workOrderType)                                  e.workOrderType = 'Work order type is required.';
    if (!form.priority)                                       e.priority      = 'Priority is required.';
    if (!form.scheduledDate)                                  e.scheduledDate = 'Scheduled date is required.';
    if (!form.description.trim())                             e.description   = 'Description is required.';
    if (form.estimatedDurationHours < 0.5)                   e.estimatedDurationHours = 'Minimum duration is 0.5 hours.';
    if (form.estimatedCost < 0)                              e.estimatedCost = 'Cost cannot be negative.';
    return e;
  }, [form]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!isMounted.current) return;
    setSubmitting(true);
    setSubmitError(null);

    // Build the work order payload
    const payload = {
      asset_id:                  prediction?.assetId,
      asset_name:                prediction?.assetName,
      asset_type:                prediction?.assetType,
      work_order_type:           form.workOrderType,
      priority:                  form.priority,
      scheduled_date:            form.scheduledDate,
      scheduled_time:            form.scheduledTime,
      assigned_technician:       form.assignedTechnician,
      estimated_duration_hours:  Number(form.estimatedDurationHours),
      estimated_cost:            Number(form.estimatedCost),
      description:               form.description.trim(),
      notes:                     form.notes.trim(),
      // Metadata — traceability
      source:                    'problem_assets_widget',
      risk_score:                prediction?.riskScore   || 0,
      health_status:             prediction?.healthStatus || 'unknown',
      created_at:                new Date().toISOString(),
    };

    try {
      await onSubmit(payload);  // parent handles API call + modal close on success
      if (isMounted.current) setSubmitDone(true);
    } catch (err) {
      // Parent throws on failure — display inline, keep modal open
      if (isMounted.current) {
        setSubmitError(err?.response?.data?.message || err?.message || 'Failed to create work order. Please try again.');
      }
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  }, [form, validate, prediction, onSubmit]);

  // ── Priority colour helper ─────────────────────────────────────────────────
  const prioColor = PRIORITIES.find(p => p.value === form.priority)?.color || 'gray';
  const typeConfig = WORK_ORDER_TYPES.find(t => t.value === form.workOrderType);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={cardBg} borderRadius="xl" mx={4}>

        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} pb={4}>
          <HStack spacing={3}>
            <Box p={2} bg="red.50" borderRadius="lg">
              <Icon as={MdBuild} boxSize={5} color="red.500" />
            </Box>
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="md" fontWeight="bold" color={headingColor}>Schedule Maintenance</Text>
              <Text fontSize="xs" color={mutedColor} fontWeight="normal">
                Create a work order for asset intervention
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        {/* ── BODY ────────────────────────────────────────────────────────── */}
        <ModalBody py={5}>
          <VStack align="stretch" spacing={5}>

            {/* Asset severity summary */}
            {prediction && (
              <SeverityBanner prediction={prediction} statBg={statBg} borderColor={borderColor} />
            )}

            {/* Inline submit error */}
            {submitError && (
              <Alert status="error" borderRadius="lg" variant="subtle">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">Work Order Creation Failed</AlertTitle>
                  <AlertDescription fontSize="xs">{submitError}</AlertDescription>
                </Box>
                <IconButton
                  icon={<MdClose />}
                  size="xs"
                  variant="ghost"
                  ml="auto"
                  onClick={() => setSubmitError(null)}
                  aria-label="Dismiss"
                />
              </Alert>
            )}

            {/* Success state */}
            {submitDone && (
              <Alert status="success" borderRadius="lg" variant="subtle">
                <AlertIcon />
                <AlertTitle fontSize="sm">Work Order Created Successfully</AlertTitle>
              </Alert>
            )}

            <Divider />

            {/* ── Row 1: Work order type + Priority ─────────────────────── */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.workOrderType}>
                <FormLabel fontSize="sm" color={labelColor} mb={1}>
                  <HStack spacing={1}>
                    <Icon as={MdBuild} boxSize={3} />
                    <Text>Work Order Type</Text>
                  </HStack>
                </FormLabel>
                <Select
                  value={form.workOrderType}
                  onChange={e => setField('workOrderType', e.target.value)}
                  size="sm"
                  borderRadius="md"
                  isDisabled={submitting}
                >
                  {WORK_ORDER_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
                {typeConfig && (
                  <FormHelperText fontSize="10px">{typeConfig.description}</FormHelperText>
                )}
                <FormErrorMessage fontSize="xs">{errors.workOrderType}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.priority}>
                <FormLabel fontSize="sm" color={labelColor} mb={1}>
                  <HStack spacing={1}>
                    <Icon as={MdPriorityHigh} boxSize={3} />
                    <Text>Priority</Text>
                    <Badge colorScheme={prioColor} fontSize="9px" ml={1}>{form.priority?.toUpperCase()}</Badge>
                  </HStack>
                </FormLabel>
                <Select
                  value={form.priority}
                  onChange={e => setField('priority', e.target.value)}
                  size="sm"
                  borderRadius="md"
                  isDisabled={submitting}
                >
                  {PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </Select>
                <FormHelperText fontSize="10px">
                  Auto-set from asset severity score ({prediction?.riskScore ?? '—'}/100)
                </FormHelperText>
                <FormErrorMessage fontSize="xs">{errors.priority}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            {/* ── Row 2: Date + Time ────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired isInvalid={!!errors.scheduledDate}>
                <FormLabel fontSize="sm" color={labelColor} mb={1}>
                  <HStack spacing={1}>
                    <Icon as={MdSchedule} boxSize={3} />
                    <Text>Scheduled Date</Text>
                  </HStack>
                </FormLabel>
                <Input
                  type="date"
                  value={form.scheduledDate}
                  min={todayISO()}
                  onChange={e => setField('scheduledDate', e.target.value)}
                  size="sm"
                  borderRadius="md"
                  isDisabled={submitting}
                />
                <FormErrorMessage fontSize="xs">{errors.scheduledDate}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" color={labelColor} mb={1}>Scheduled Time</FormLabel>
                <Input
                  type="time"
                  value={form.scheduledTime}
                  onChange={e => setField('scheduledTime', e.target.value)}
                  size="sm"
                  borderRadius="md"
                  isDisabled={submitting}
                />
              </FormControl>
            </SimpleGrid>

            {/* ── Row 3: Technician + Duration ─────────────────────────── */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" color={labelColor} mb={1}>
                  <HStack spacing={1}>
                    <Icon as={MdPerson} boxSize={3} />
                    <Text>Assign Technician</Text>
                  </HStack>
                </FormLabel>
                <Select
                  value={form.assignedTechnician}
                  onChange={e => setField('assignedTechnician', e.target.value)}
                  size="sm"
                  borderRadius="md"
                  isDisabled={submitting}
                >
                  {TECHNICIANS.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isInvalid={!!errors.estimatedDurationHours}>
                <FormLabel fontSize="sm" color={labelColor} mb={1}>
                  <HStack spacing={1}>
                    <Icon as={MdTimer} boxSize={3} />
                    <Text>Est. Duration (hours)</Text>
                  </HStack>
                </FormLabel>
                <NumberInput
                  value={form.estimatedDurationHours}
                  min={0.5}
                  max={96}
                  step={0.5}
                  precision={1}
                  size="sm"
                  isDisabled={submitting}
                  onChange={val => setField('estimatedDurationHours', parseFloat(val) || 0)}
                >
                  <NumberInputField borderRadius="md" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormErrorMessage fontSize="xs">{errors.estimatedDurationHours}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            {/* ── Est. Cost ────────────────────────────────────────────── */}
            <FormControl isInvalid={!!errors.estimatedCost}>
              <FormLabel fontSize="sm" color={labelColor} mb={1}>
                <HStack spacing={1}>
                  <Icon as={MdAttachMoney} boxSize={3} />
                  <Text>Estimated Cost ($)</Text>
                </HStack>
              </FormLabel>
              <NumberInput
                value={form.estimatedCost}
                min={0}
                precision={2}
                size="sm"
                isDisabled={submitting}
                onChange={val => setField('estimatedCost', parseFloat(val) || 0)}
              >
                <NumberInputField borderRadius="md" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="10px">
                Pre-filled from asset's total maintenance cost history
              </FormHelperText>
              <FormErrorMessage fontSize="xs">{errors.estimatedCost}</FormErrorMessage>
            </FormControl>

            {/* ── Description ──────────────────────────────────────────── */}
            <FormControl isRequired isInvalid={!!errors.description}>
              <FormLabel fontSize="sm" color={labelColor} mb={1}>
                <HStack spacing={1}>
                  <Icon as={MdNotes} boxSize={3} />
                  <Text>Description</Text>
                </HStack>
              </FormLabel>
              <Textarea
                placeholder={`Describe the maintenance action required for ${prediction?.assetName || 'this asset'}...`}
                value={form.description}
                onChange={e => setField('description', e.target.value)}
                size="sm"
                borderRadius="md"
                rows={3}
                isDisabled={submitting}
              />
              <FormHelperText fontSize="10px">
                {form.description.length}/500 characters
              </FormHelperText>
              <FormErrorMessage fontSize="xs">{errors.description}</FormErrorMessage>
            </FormControl>

            {/* ── Notes ────────────────────────────────────────────────── */}
            <FormControl>
              <FormLabel fontSize="sm" color={labelColor} mb={1}>
                Internal Notes <Text as="span" color={mutedColor} fontWeight="normal">(optional)</Text>
              </FormLabel>
              <Textarea
                placeholder="Additional context for the assigned technician..."
                value={form.notes}
                onChange={e => setField('notes', e.target.value)}
                size="sm"
                borderRadius="md"
                rows={2}
                isDisabled={submitting}
              />
            </FormControl>

            {/* ── Work order summary card ───────────────────────────────── */}
            <Box p={4} bg={statBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor}>
              <HStack mb={3}>
                <Icon as={MdInfo} color="blue.500" boxSize={4} />
                <Text fontWeight="semibold" fontSize="xs" color={headingColor} textTransform="uppercase" letterSpacing="wide">
                  Work Order Summary
                </Text>
              </HStack>
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} fontSize="xs">
                {[
                  { label: 'Asset',    value: prediction?.assetName || '—'          },
                  { label: 'Type',     value: typeConfig?.label || form.workOrderType },
                  { label: 'Priority', value: form.priority?.toUpperCase(),
                    badge: true, scheme: prioColor                                   },
                  { label: 'Date',     value: form.scheduledDate ? new Date(form.scheduledDate + 'T' + form.scheduledTime).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
                  { label: 'Technician', value: form.assignedTechnician              },
                  { label: 'Est. Cost', value: `$${Number(form.estimatedCost).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                ].map(({ label, value, badge, scheme }) => (
                  <Box key={label}>
                    <Text color={mutedColor} mb={0.5}>{label}</Text>
                    {badge
                      ? <Badge colorScheme={scheme} fontSize="xs" borderRadius="full" px={2}>{value}</Badge>
                      : <Text fontWeight="semibold" color={headingColor} noOfLines={1}>{value}</Text>
                    }
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

          </VStack>
        </ModalBody>

        {/* ── FOOTER ────────────────────────────────────────────────────────── */}
        <ModalFooter borderTopWidth="1px" borderColor={borderColor} pt={3}>
          <HStack spacing={3} w="full" justify="space-between">
            <Text fontSize="xs" color={mutedColor}>
              <Icon as={MdInfo} boxSize={3} mr={1} />
              Work order will be created in FlowSense and assigned to the technician queue.
            </Text>
            <HStack spacing={2}>
              <Button size="sm" variant="ghost" onClick={onClose} isDisabled={submitting}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                leftIcon={<Icon as={MdBuild} />}
                isLoading={submitting}
                loadingText="Creating..."
                onClick={handleSubmit}
                isDisabled={submitDone}
              >
                {submitDone ? '✓ Created' : 'Create Work Order'}
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MaintenanceActionModal;